import * as DF from 'date-fns';
import Util from './Util';

/**
 * Formats a given number.
 *
 * @param {Number|String} num
 * @param {Object} [userOpts] User configuration
 * @returns {String}
 */
function number(num, userOpts) {
    if (num === undefined) return '';
    const defaults = {
        decimals: 2,
        prefix: '',
        suffix: '',
        separator: {
            decimal: '.',
            thousand: ','
        }
    };

    num = num || 0;
    userOpts = userOpts || {};
    const opts = Util.extend(defaults, userOpts);

    // Cast the string to a number.
    if (typeof num === 'string') {
        num *= 1;
    }

    if (!isFinite(num)) {
        return num.toString();
    }
    num = num.toFixed(opts.decimals);

    // Replace the dot.
    if (opts.separator.decimal !== '.') num = num.replace(/\./, opts.separator.decimal);

    // Set the thousand separator.
    const numParts = num.split(opts.separator.decimal);
    numParts[0] = numParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, opts.separator.thousand);
    num = opts.prefix + numParts.join(opts.separator.decimal) + opts.suffix;

    return num;
}

/**
 * Format number as currency.
 *
 * @param {*} value Cell value.
 * @returns {String}
 */
function currency(value) {
    const vue = window.jmpVueInst;
    if (!vue) return '';
    const locale = vue.$_FORMAT_LOCALE;

    if (locale === 'en-ZA') {
        return number(value, {
            decimals: 2,
            prefix: 'R'
        });
    }
    return vue.$n(value, 'currency', locale);
}

/**
 * Formatter number as currency without cents.
 *
 * @param {*} value Cell value.
 * @returns {String}
 */
function currencyInt(value) {
    return number(value, {
        decimals: 0,
        prefix: 'R'
    });
}

function dateLong(value) {
    const vue = window.mfVueInst;
    if (!vue) return '';
    const locale = vue.$_FORMAT_LOCALE;

    if (typeof value === 'string' &&
        (value.length === 24 || value.length === 19) &&
        value[13] === ':') value = new Date(value);
    if (!DF.isDate(value)) return value;
    return vue.$d(value, 'long', locale);
}

/**
 * Element UI table formatter for short dates.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function dateShort(value) {
    const vue = window.jmpVueInst;
    if (!vue) return '';
    const locale = vue.$_FORMAT_LOCALE;

    if (typeof value === 'string' &&
        (value.length === 24 || value.length === 19) &&
        value[13] === ':') value = new Date(value);
    if (!DF.isDate(value)) return value;
    /* if (locale === 'en-ZA') {
        return DF.format(value, 'dd MMMM yyyy');
    } */
    return vue.$d(value, 'short', locale);
}

/**
 * Element UI table formatter for date & time.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function dateTime(value) {
    const vue = window.jmpVueInst;
    if (!vue) return '';
    const locale = vue.$_FORMAT_LOCALE;

    if (typeof value === 'string' &&
        (value.length === 24 || value.length === 19) &&
        value[13] === ':') value = new Date(value);
    if (!DF.isDate(value)) return value;
    /* if (locale === 'en-ZA') {
        return DF.format(value, 'dd MMMM yyyy');
    } */
    // return DF.format(value, 'yyyy-MM-dd');
    return vue.$d(value, 'long+time', locale);
}

/**
 * Format number as integer.
 *
 * @param {*} value Cell value.
 * @returns {String}
 */
function int(value) {
    const vue = window.jmpVueInst;
    if (!vue) return '';
    const locale = vue.$_FORMAT_LOCALE;

    if (typeof value !== 'number') return value;
    if (locale === 'en-ZA') {
        return number(value, {
            decimals: 0
        });
    }
    return vue.$n(value, 'int', locale);
}

/**
 * Element UI table formatter for integers. If zero return a -
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function intZeroDash(value) {
    if (!value) return '-';
    return int(value);
}

/**
 * Element UI table formatter for integers. If zero return a blank string.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function intZeroEmpty(value) {
    if (!value) return '';
    return int(value);
}

/**
 * Element UI table formatter for integer kilometers.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function km(value) {
    return number(value, {
        decimals: 0,
        suffix: ' km',
    });
}

function time(value) {
    /* const i = parseInt(value, 10);
    const frac = value - i;
    const mins = `${60 * frac}00`.substr(0, 2);
    return `${i}:${mins}`; */

    if (typeof value !== 'number') return '0:00';
    value += ''; // Make a string.
    if (value.indexOf('.') > -1) { // Float.
        value = value.split('.');
        if ((!value[0] || value[0] === '0') && (!value[1] || value[1] === '0')) {
            value = [];
        }
        else {
            if (!value[0]) value[0] = '0';
            if (!value[1] || value[1] === '0') {
                value[1] = '00';
            }
            else {
                let m = Math.round(60 * parseFloat(`0.${value[1]}`));
                if (m < 1 && (!value[0] || value[0] === '0')) {
                    value = [];
                }
                else {
                    if (m < 10) m = `0${m}`;
                    value[1] = `${m}00`.substr(0, 2);
                }
            }
        }
    }
    else { // Integer.
        value = (!value || value === '0') ? [] : [value, '00'];
    }
    return value.length ? value.join(':') : '0:00';
}

/**
 * Element UI table formatter for integer percentage.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function percent(value) {
    return number(value, {
        decimals: 2,
        suffix: '%',
    });
}

/**
 * Element UI table formatter for integer percentage.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function percentInt(value) {
    return number(value, {
        decimals: 0,
        suffix: '%',
    });
}

/**
 * Element UI table formatter for boolean to Yes or No.
 *
 * @param {*} rec Data record.
 * @param {*} col Column definition.
 * @param {*} value Cell value.
 * @returns {String}
 */
function yesNo(value) {
    return value ? 'Yes' : 'No';
}

export default {
    number,
    currency,
    currencyInt,
    int,
    dateLong,
    dateShort,
    dateTime,
    intZeroDash,
    intZeroEmpty,
    km,
    time,
    percent,
    percentInt,
    yesNo
};



// WEBPACK FOOTER //
// ./src/util/Formatter.js